import React, { useEffect, useRef, useState } from 'react';

// Players from the platforms themselves: we link and embed, we never re-host the videos.
export function embedUrl(l, muted = false) {
  if (l.platform === 'tiktok') {
    const m = l.url.match(/\/(video|photo)\/(\d+)/);
    if (!m) return null;
    return m[1] === 'video'
      ? `https://www.tiktok.com/player/v1/${m[2]}?autoplay=1&rel=0&description=0&music_info=0${muted ? '&muted=1&loop=1' : ''}`
      : `https://www.tiktok.com/embed/v2/${m[2]}`;
  }
  const href = encodeURIComponent(l.url);
  return l.media_kind === 'video'
    ? `https://www.facebook.com/plugins/video.php?href=${href}&show_text=false&autoplay=true${muted ? '&mute=true&muted=true' : ''}`
    : `https://www.facebook.com/plugins/post.php?href=${href}&show_text=true&width=380`;
}

export const isVideo = (l) => l.media_kind === 'video';

// A post that opens as a wide embed (photo posts) rather than a 9:16 video.
export const isWide = (l) => !isVideo(l) || (l.platform === 'tiktok' && /\/photo\//.test(l.url));

export function EmbedFrame({ listing, muted = false, className = '' }) {
  const ref = useRef(null);
  const src = embedUrl(listing, muted);

  // TikTok's player takes mute/unMute commands by postMessage; Facebook's only the URL flag.
  useEffect(() => {
    const iframe = ref.current;
    if (!iframe || listing.platform !== 'tiktok') return undefined;
    const send = () => {
      try {
        iframe.contentWindow.postMessage({ 'x-tiktok-player': true, type: muted ? 'mute' : 'unMute' }, '*');
      } catch (e) { /* player not ready */ }
    };
    const timers = [];
    const onLoad = () => { send(); timers.push(setTimeout(send, 800), setTimeout(send, 2000)); };
    iframe.addEventListener('load', onLoad);
    return () => { iframe.removeEventListener('load', onLoad); timers.forEach(clearTimeout); };
  }, [listing.platform, muted, src]);

  if (!src) return null;
  return (
    <iframe
      ref={ref}
      src={src}
      title={`Annonce de ${listing.account}`}
      allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
      className={`absolute inset-0 w-full h-full border-0 ${className}`}
    />
  );
}

// A viewer preference (hover play, autoplay, sound) remembered in this browser only.
export function usePref(key, fallback) {
  const [value, setValue] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? fallback : v === '1';
    } catch (e) {
      return fallback;
    }
  });
  const update = (v) => {
    setValue(v);
    try { localStorage.setItem(key, v ? '1' : '0'); } catch (e) { /* storage blocked */ }
  };
  return [value, update];
}

export const canHover = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(hover: hover)').matches;

export function formatDuration(s) {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function Toggle({ on, onChange, children, dark = false }) {
  const base = dark
    ? (on ? 'text-orange-300 border-orange-400' : 'text-stone-300 border-white/20 bg-white/10')
    : (on ? 'text-blue-700 border-blue-500 bg-white' : 'text-gray-500 border-gray-300 bg-white');
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={() => onChange(!on)}
      className={`inline-flex items-center gap-2 text-sm border rounded-lg px-3 py-2 ${base}`}
    >
      <span className={`relative w-7 h-4 rounded-full transition-colors ${on ? (dark ? 'bg-orange-400' : 'bg-blue-600') : 'bg-gray-300'}`}>
        <span className={`absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${on ? 'translate-x-3' : ''}`} />
      </span>
      {children}
    </button>
  );
}
